import { createApp, type App } from 'vue'
import QrCodePayment from '../views/qrcode/QrCodePayment.vue'
import { setupI18n } from '../i18n'
import type { CreatePaymentDropinOrderRes, PaymentOrderInfo, PublishPaymentMethodItem } from '../apis/types/payment'
import { createPaymentDropinOrder } from '../apis/payment'
import { PAYMENT_APP_VERSION } from '../const'

export interface WechatPayOrderResult extends CreatePaymentDropinOrderRes {
  result_data: {
    qr_code: string
    /** 二维码过期时间，毫秒时间戳。 */
    expire_time_at: number
  }
}

export interface WechatPayDropInConfig {
  clientId: string
  language?: string
  sdkVersion?: string
  orderInfo: PaymentOrderInfo
  onSubmit?: () => void
  onError?: (error: unknown) => void
  onCreateOrder?: (result: WechatPayOrderResult) => void
  onCompleted?: () => void
}

/** 独立微信扫码渠道；建单、查单使用项目支付接口。 */
export const createWechatPayDropIn = (config: WechatPayDropInConfig) => {
  let app: App | undefined
  let generation = 0

  const destroy = () => {
    generation++
    app?.unmount()
    app = undefined
  }

  const setup = (containerId: string, paymentMethod: PublishPaymentMethodItem) => {
    if (paymentMethod.payment_channel_type !== 13 || paymentMethod.payment_type !== 2) {
      throw new Error('Wechat Pay requires payment channel 13 and dropin payment type 2')
    }
    if (!config.orderInfo?.sys_order_id || !paymentMethod.payment_method_id) {
      throw new Error('Wechat Pay requires sys_order_id and payment_method_id')
    }

    destroy()
    const currentGeneration = generation
    const isActive = () => currentGeneration === generation
    const paymentMethodId = paymentMethod.payment_method_id
    const createOrder = async (): Promise<WechatPayOrderResult> => {
      const result = await createPaymentDropinOrder({
        dropin_channel_data: JSON.stringify({}),
        payment_channel_type: 13,
        payment_method_id: paymentMethodId,
        sys_order_id: config.orderInfo.sys_order_id,
        sdk_version: config.sdkVersion || PAYMENT_APP_VERSION,
      })
      if (!isActive()) throw new Error('Wechat Pay component has been destroyed')

      const data = result?.result_data
      if (typeof result?.payment_id !== 'string' || !result.payment_id.trim()
        || typeof data?.qr_code !== 'string' || !data.qr_code.trim()
        || !Number.isFinite(data.expire_time_at) || data.expire_time_at <= 0) {
        throw new Error('Invalid Wechat Pay QR order response')
      }
      config.onCreateOrder?.(result)
      return result
    }

    app = createApp(QrCodePayment, {
      orderInfo: config.orderInfo,
      createOrder,
      onSubmit: () => { if (isActive()) config.onSubmit?.() },
      onError: (error: unknown) => { if (isActive()) config.onError?.(error) },
      onCompleted: () => { if (isActive()) config.onCompleted?.() },
    })
    try {
      setupI18n(app)
      app.mount(containerId)
    } catch (error) {
      destroy()
      throw error
    }

    return { destroy: () => { if (isActive()) destroy() } }
  }

  return { setup, destroy }
}
